import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Dialog, Button } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import { useQuery } from '@/lib/useQuery';
import { errorText } from '@/lib/format';
import { applyBrand } from '@/theme/applyTheme';
import { applyStoreTheme } from '@/theme/storeTheme';
import { cartLine, emptyCart, sameLine } from './lib/cart';
import { toggleCompare } from './lib/compare';
import { track } from './lib/beacon';
import { buildShop } from './lib/shop';
import { withCustom } from '@/lib/productTypes';
import { chromeOf, storeDocOf } from './lib/design';
import { linkAllowed, withoutClosedLinks } from './lib/features';

/**
 * Storefront-wide state: the REAL published catalog (fetched once), the
 * fixture-backed cart and customer session, the compare selection and the
 * customer's delivery PIN. Pages read it through useStorefront().
 */
const Ctx = createContext(null);

const readLS = (key, fallback) => {
  try {
    const v = JSON.parse(localStorage.getItem(key) || 'null');
    return v === null ? fallback : v;
  } catch {
    return fallback;
  }
};
const writeLS = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable: keep in memory only */
  }
};

/** A draft preview link (?preview=<token>) lasts for this browser tab, until "Exit preview". */
function previewToken() {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('preview');
    if (fromUrl) {
      sessionStorage.setItem('sf.preview', fromUrl);
      // Keep the token out of history, bookmarks and copied links.
      params.delete('preview');
      const rest = params.toString();
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`);
    }
    return sessionStorage.getItem('sf.preview') || null;
  } catch {
    return null;
  }
}

/**
 * `canVisit(href)` is false for a page behind a store feature that is off (D101, the
 * store's features and feature paths from /api/meta/context), so no link to it is shown,
 * the owner's links in the store document included.
 */
export function StorefrontProvider({ store, children }) {
  const { fetchApi } = useAppApi();
  const catalog = useQuery('storefront', { limit: 60 });
  const [preview] = useState(previewToken);
  const look = useQuery('sfAppearance', { preview }, { select: (r) => r.data });
  const appearance = look.data || null;
  const mediaUrl = useCallback((ref) => (ref && appearance && appearance.media[String(ref.media_id)]) || null, [appearance]);
  const canVisit = useMemo(() => linkAllowed(store), [store]);
  // The whole storefront design (D64): landing page, listing/product templates, header/footer, card.
  const doc = useMemo(() => withoutClosedLinks(storeDocOf(appearance), canVisit), [appearance, canVisit]);
  const chrome = useMemo(() => chromeOf(doc), [doc]);
  const listings = useMemo(() => (catalog.data && catalog.data.data) || [], [catalog.data]);
  const brand = (catalog.data && catalog.data.store) || {};
  // Product types (D32) and the store's categories (B11-P3): routes, nav, filters and specs come from these.
  const registryQ = useQuery('productTypes', undefined, { select: (r) => r });
  // Each type carries the store's own fields (ADR 0009 §4, MERCH-86): product page rows and shop filters.
  const shop = useMemo(() => {
    const own = (catalog.data && catalog.data.custom_attributes) || {};
    const types = ((registryQ.data && registryQ.data.types) || []).map((t) => withCustom(t, own[t.key]));
    return buildShop(types, listings, (catalog.data && catalog.data.categories) || []);
  }, [registryQ.data, listings, catalog.data]);
  const seller = useMemo(() => ({ slug: (store && store.slug) || 'store', name: brand.name || (store && store.name) || 'Store' }), [store, brand.name]);

  const [cart, setCart] = useState(emptyCart());
  const [session, setSession] = useState(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [compareIds, setCompareIds] = useState(() => readLS('sf.compare', []));
  const [pin, setPinState] = useState(() => readLS('sf.pin', ''));
  const [conflict, setConflict] = useState(null);

  useEffect(() => {
    document.title = seller.name;
  }, [seller.name]);

  useEffect(() => {
    // The landing page's theme (D26) is the source of truth; the old brand.primary setting only until it loads.
    if (appearance) applyStoreTheme(appearance.theme, { mediaUrl });
    else if (brand.primary) applyBrand({ '--color-primary-600': brand.primary });
  }, [appearance, mediaUrl, brand.primary]);

  useEffect(() => {
    fetchApi('sfCart', {}, false).then((r) => setCart(r.data)).catch(() => {});
    fetchApi('sfSession', {}, false)
      .then((r) => setSession(r.data))
      .catch(() => {})
      .finally(() => setSessionReady(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addToCart = useCallback(
    // `variant`: the chosen variant of a listing with options (MERCH-87); the server refuses one without.
    async (listing, qty = 1, variant = null) => {
      try {
        const line = { ...cartLine(listing), ...(variant && variant.id != null ? { variant_id: variant.id, variant_label: variant.label || null } : {}), qty };
        const r = await fetchApi('sfCartAdd', { line, seller }, false);
        setCart(r.data);
        track('add_to_cart', { listingId: listing.id });
        return { ok: true };
      } catch (ex) {
        const code = ex && ex.response && ex.response.data && ex.response.data.code;
        if (ex && ex.response && ex.response.status === 409 && code !== 'STOCK_UNAVAILABLE') setConflict({ listing, variant, message: errorText(ex) });
        return { ok: false, error: errorText(ex) };
      }
    },
    [fetchApi, seller]
  );

  const removeFromCart = useCallback(async (listingId, variantId = null) => {
    const r = await fetchApi('sfCartRemove', { listing_id: listingId, variant_id: variantId }, false);
    setCart(r.data);
  }, [fetchApi]);

  /** Items sold by quantity: the server clamps to what is in stock and answers with the cart. */
  const setCartQty = useCallback(async (listingId, qty, variantId = null) => {
    try {
      const r = await fetchApi('sfCartSetQty', { listing_id: listingId, qty, variant_id: variantId }, false);
      setCart(r.data);
      return { ok: true };
    } catch (ex) {
      return { ok: false, error: errorText(ex) };
    }
  }, [fetchApi]);

  const refreshCart = useCallback(async () => {
    const r = await fetchApi('sfCart', {}, false);
    setCart(r.data);
    return r.data;
  }, [fetchApi]);

  const startSeparateOrder = async () => {
    const pending = conflict;
    setConflict(null);
    await fetchApi('sfCartClear', {}, false);
    if (pending) await addToCart(pending.listing, 1, pending.variant);
  };

  /** Returns an error sentence when the compare limit is hit, else null. */
  const toggle = useCallback(
    (id) => {
      // Compare stays within one product type (B11-P3).
      const r = toggleCompare(compareIds, id, undefined, (x) => {
        const l = listings.find((it) => Number(it.id) === Number(x));
        return l ? l.product_type || null : null;
      });
      if (!r.error) {
        writeLS('sf.compare', r.ids);
        setCompareIds(r.ids);
      }
      return r.error;
    },
    [compareIds, listings]
  );

  const setCompare = useCallback((ids) => {
    writeLS('sf.compare', ids);
    setCompareIds(ids);
  }, []);

  const setPin = useCallback((p) => {
    writeLS('sf.pin', p);
    setPinState(p);
  }, []);

  const logout = useCallback(async () => {
    await fetchApi('sfLogout', {}, false);
    setSession(null);
  }, [fetchApi]);

  const value = {
    store,
    canVisit,
    seller,
    brand: { name: seller.name, tagline: brand.tagline || `Inspected ${shop.noun} with warranty, from a verified reseller` },
    catalog,
    listings,
    shop,
    byId: (id) => listings.find((l) => String(l.id) === String(id)) || null,
    cart,
    // With a variant id: that variant's line (MERCH-87); without, any line of the listing.
    inCart: (id, variantId = null) => cart.items.some((i) => sameLine(i, id, variantId)),
    addToCart,
    removeFromCart,
    setCartQty,
    refreshCart,
    compareIds,
    toggleCompare: toggle,
    setCompare,
    session,
    sessionReady,
    setSession,
    logout,
    pin,
    setPin,
    // Landing page + theme (D24–D26) and image URLs for it; `previewing` while showing a draft.
    appearance,
    appearanceQuery: look,
    doc,
    chrome,
    card: doc.card || {},
    mediaUrl,
    previewing: !!(appearance && appearance.preview),
    exitPreview: () => {
      try {
        sessionStorage.removeItem('sf.preview');
      } catch {
        /* nothing stored */
      }
      window.location.replace(window.location.pathname);
    },
  };

  return (
    <Ctx.Provider value={value}>
      {children}
      <Dialog
        isOpen={!!conflict}
        onClose={() => setConflict(null)}
        title="One seller per order"
        footer={
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={() => setConflict(null)}>Keep my cart</Button>
            <Button onClick={startSeparateOrder}>Start a separate order</Button>
          </div>
        }
      >
        <p className="text-sm">{conflict && conflict.message}</p>
      </Dialog>
    </Ctx.Provider>
  );
}

export function useStorefront() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useStorefront must be used inside <StorefrontProvider>');
  return v;
}

/**
 * The storefront context for Storefront Studio's live preview (B15): the store's
 * own listings and the draft document, with no cart, session or page-wide theme
 * side effects. Storefront components render inside it exactly as shoppers see them.
 */
export function StorefrontPreviewProvider({ doc, theme, listings = [], types = [], categories = [], name = 'Your store', mediaUrl = () => null, children }) {
  const value = useMemo(() => {
    const shop = buildShop(types, listings, categories);
    const none = () => {};
    return {
      store: null,
      // The Studio preview shows the owner's links as designed; the live store hides closed ones.
      canVisit: () => true,
      seller: { slug: 'store', name },
      brand: { name, tagline: `Inspected ${shop.noun} with warranty, from a verified reseller` },
      catalog: { data: { data: listings }, loading: false, error: null },
      listings,
      shop,
      byId: (id) => listings.find((l) => String(l.id) === String(id)) || null,
      cart: emptyCart(),
      inCart: () => false,
      addToCart: async () => ({ ok: false, error: 'Shoppers add to cart on the live storefront.' }),
      removeFromCart: none,
      setCartQty: none,
      refreshCart: none,
      compareIds: [],
      toggleCompare: () => null,
      setCompare: none,
      session: null,
      sessionReady: true,
      setSession: none,
      logout: none,
      pin: '',
      setPin: none,
      appearance: { theme, media: {} },
      appearanceQuery: { data: null, loading: false, error: null },
      doc,
      chrome: chromeOf(doc),
      card: doc.card || {},
      mediaUrl,
      previewing: false,
      exitPreview: none,
    };
  }, [doc, theme, listings, types, categories, name, mediaUrl]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
